(function () {
  const STATE_STORAGE_KEY = "fs_sidebar_state_v2";
  const SCROLL_STORAGE_KEY = "fs_sidebar_scroll_v1";

  function initFrameSpaceSidebar() {
    document.documentElement.style.removeProperty("--sidebar-display");

    const layout = document.querySelector(".appLayout");
    const sidebar = document.getElementById("appSidebar");

    if (!layout || !sidebar) {
      document.documentElement.classList.add("fs-sidebar-ready");
      return;
    }

    if (window.__frameSpaceSidebarAbortController) {
      window.__frameSpaceSidebarAbortController.abort();
    }

    const abortController = new AbortController();
    const signal = abortController.signal;
    window.__frameSpaceSidebarAbortController = abortController;

    const mobileQuery = window.matchMedia("(max-width: 1000px)");
    const defaultState = sidebar.dataset.defaultState === "collapsed" ? "collapsed" : "expanded";
    const showTooltips = sidebar.dataset.showTooltips !== "false";
    const nav = sidebar.querySelector(".appSidebar__nav");

    let tooltip = document.querySelector(".appSidebarTooltip");
    let tooltipTarget = null;
    let scrollSaveFrame = 0;

    const getToggles = () => Array.from(document.querySelectorAll("[data-sidebar-toggle]"));

    function storageGet(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    function storageSet(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        // The sidebar remains usable when storage is blocked.
      }
    }

    function isCollapsed() {
      return layout.classList.contains("sidebarCollapsed");
    }

    function isMobileOpen() {
      return layout.classList.contains("sidebarOpen");
    }

    function updateToggleState() {
      getToggles().forEach((toggle) => {
        if (mobileQuery.matches) {
          const open = isMobileOpen();
          toggle.setAttribute("aria-expanded", open ? "true" : "false");
          toggle.setAttribute("aria-label", open ? "Close sidebar" : "Open sidebar");
        } else {
          const expanded = !isCollapsed();
          toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
          toggle.setAttribute("aria-label", expanded ? "Collapse sidebar" : "Expand sidebar");
        }
      });
    }

    function closeTooltip() {
      tooltipTarget = null;

      if (tooltip) {
        tooltip.classList.remove("is-visible");
        tooltip.setAttribute("aria-hidden", "true");
      }
    }

    function ensureTooltip() {
      if (tooltip) return tooltip;

      tooltip = document.createElement("div");
      tooltip.className = "appSidebarTooltip";
      tooltip.setAttribute("role", "tooltip");
      tooltip.setAttribute("aria-hidden", "true");
      document.body.appendChild(tooltip);

      return tooltip;
    }

    function showTooltip(target) {
      if (!showTooltips || mobileQuery.matches || !isCollapsed()) return;

      const label = target.dataset.sidebarLabel;
      if (!label) return;

      const node = ensureTooltip();
      const rect = target.getBoundingClientRect();

      tooltipTarget = target;
      node.textContent = label;
      node.style.left = `${Math.round(rect.right + 12)}px`;
      node.style.top = `${Math.round(rect.top + rect.height / 2)}px`;
      node.setAttribute("aria-hidden", "false");

      window.requestAnimationFrame(() => {
        if (tooltipTarget === target) {
          node.classList.add("is-visible");
        }
      });
    }

    function prepareLabels() {
      sidebar.querySelectorAll(".appSidebar__item, .appSidebar__footerAction").forEach((item) => {
        const labelNode = item.querySelector(".appSidebar__itemLabel, .appSidebar__footerLabel");
        const label = labelNode ? labelNode.textContent.trim() : "";

        if (!label) return;

        item.dataset.sidebarLabel = label;

        if (!item.hasAttribute("aria-label")) {
          item.setAttribute("aria-label", label);
        }
      });
    }

    function syncActiveAiMode() {
      const currentPath = window.location.pathname.replace(/\/+$/, "");
      if (!currentPath.endsWith("/pages/framespace-ai")) return;

      const activeMode = new URLSearchParams(window.location.search).get("mode") || "image";

      sidebar.querySelectorAll("[data-ai-mode]").forEach((link) => {
        if (link.getAttribute("data-ai-mode") === activeMode) {
          link.setAttribute("aria-current", "page");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    }

    function saveScrollPosition() {
      if (!nav) return;
      storageSet(SCROLL_STORAGE_KEY, String(Math.max(0, Math.round(nav.scrollTop))));
    }

    function queueScrollPositionSave() {
      closeTooltip();

      if (scrollSaveFrame) return;

      scrollSaveFrame = window.requestAnimationFrame(() => {
        scrollSaveFrame = 0;
        saveScrollPosition();
      });
    }

    function restoreScrollPosition() {
      if (!nav) return;

      const storedValue = Number.parseFloat(storageGet(SCROLL_STORAGE_KEY));
      const hasStoredValue = Number.isFinite(storedValue) && storedValue >= 0;

      const applyPosition = () => {
        if (hasStoredValue) {
          const maximum = Math.max(0, nav.scrollHeight - nav.clientHeight);
          nav.scrollTop = Math.min(storedValue, maximum);
          return;
        }

        const activeItem = nav.querySelector('[aria-current="page"]');

        if (activeItem) {
          const target = activeItem.offsetTop - ((nav.clientHeight - activeItem.offsetHeight) / 2);
          nav.scrollTop = Math.max(0, target);
        }
      };

      applyPosition();
      window.requestAnimationFrame(() => {
        applyPosition();
        window.requestAnimationFrame(applyPosition);
      });
    }

    function setMobileOpen(open) {
      layout.classList.toggle("sidebarOpen", open);
      document.documentElement.classList.toggle("fs-sidebar-open", open);
      updateToggleState();

      if (!open) closeTooltip();
    }

    function syncSidebarMode() {
      closeTooltip();

      if (mobileQuery.matches) {
        layout.classList.remove("sidebarCollapsed");
        setMobileOpen(false);
        return;
      }

      document.documentElement.classList.remove("fs-sidebar-open");
      layout.classList.remove("sidebarOpen");

      const storedState = storageGet(STATE_STORAGE_KEY);
      const resolvedState = storedState === "collapsed" || storedState === "expanded"
        ? storedState
        : defaultState;

      layout.classList.toggle("sidebarCollapsed", resolvedState === "collapsed");
      updateToggleState();
    }

    function toggleSidebar() {
      closeTooltip();

      if (mobileQuery.matches) {
        setMobileOpen(!isMobileOpen());
        return;
      }

      const collapsed = !isCollapsed();
      layout.classList.toggle("sidebarCollapsed", collapsed);
      storageSet(STATE_STORAGE_KEY, collapsed ? "collapsed" : "expanded");
      updateToggleState();
    }

    prepareLabels();
    syncActiveAiMode();
    syncSidebarMode();
    restoreScrollPosition();

    document.documentElement.classList.add("fs-sidebar-ready");

    document.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-sidebar-toggle]");

      if (toggle) {
        event.preventDefault();
        toggleSidebar();
        return;
      }

      if (mobileQuery.matches && isMobileOpen() && !event.target.closest("#appSidebar")) {
        setMobileOpen(false);
      }
    }, { signal });

    sidebar.addEventListener("click", (event) => {
      const link = event.target.closest("a");

      if (link) {
        saveScrollPosition();

        if (mobileQuery.matches) {
          setMobileOpen(false);
        }
      }
    }, { signal });

    sidebar.addEventListener("pointerover", (event) => {
      const item = event.target.closest(".appSidebar__item, .appSidebar__footerAction");
      if (!item || !sidebar.contains(item)) return;
      showTooltip(item);
    }, { signal });

    sidebar.addEventListener("pointerout", (event) => {
      const item = event.target.closest(".appSidebar__item, .appSidebar__footerAction");
      if (!item) return;

      const nextTarget = event.relatedTarget;
      if (nextTarget && item.contains(nextTarget)) return;

      closeTooltip();
    }, { signal });

    sidebar.addEventListener("focusin", (event) => {
      const item = event.target.closest(".appSidebar__item, .appSidebar__footerAction");
      if (item) showTooltip(item);
    }, { signal });

    sidebar.addEventListener("focusout", closeTooltip, { signal });

    if (nav) {
      nav.addEventListener("scroll", queueScrollPositionSave, {
        passive: true,
        signal
      });
    }

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;

      if (mobileQuery.matches && isMobileOpen()) {
        setMobileOpen(false);
        document.querySelector(".appMobileSidebarToggle")?.focus();
      } else {
        closeTooltip();
      }
    }, { signal });

    window.addEventListener("pagehide", saveScrollPosition, { signal });
    window.addEventListener("beforeunload", saveScrollPosition, { signal });
    window.addEventListener("resize", closeTooltip, { passive: true, signal });

    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", () => {
        syncSidebarMode();
        restoreScrollPosition();
      }, { signal });
    } else if (typeof mobileQuery.addListener === "function") {
      if (window.__frameSpaceSidebarLegacyMediaHandler) {
        mobileQuery.removeListener(window.__frameSpaceSidebarLegacyMediaHandler);
      }

      window.__frameSpaceSidebarLegacyMediaHandler = () => {
        syncSidebarMode();
        restoreScrollPosition();
      };

      mobileQuery.addListener(window.__frameSpaceSidebarLegacyMediaHandler);
    }
  }

  function queueInit() {
    document.documentElement.style.removeProperty("--sidebar-display");
    window.requestAnimationFrame(initFrameSpaceSidebar);
  }

  document.documentElement.style.removeProperty("--sidebar-display");

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFrameSpaceSidebar, { once: true });
  } else {
    initFrameSpaceSidebar();
  }

  document.addEventListener("shopify:section:load", queueInit);
  document.addEventListener("shopify:section:select", queueInit);
  document.addEventListener("shopify:section:reorder", queueInit);
  window.addEventListener("pageshow", queueInit);
})();
